import React, { useState } from 'react';
import { RotateCcw } from 'react-feather';

import Paging from '@/shared/components/Common/Paging';
import SearchBar from '@/shared/components/Common/SearchBar';
import { useInitialPage } from '@/shared/hooks/useInitialPage';
import { usePendingApproval } from './hooks/usePendingApproval';

import PendingApprovalModal from './components/UI/PendingApprovalModal';
import {
  ApprovalStatus,
  PendingUser,
  StatusFilterOption,
} from './types/userTypes';
import UserPendingTable from './UserPendingTable';

const statusFilterOptions: StatusFilterOption[] = [
  { value: 'ALL', label: 'All Status' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
];

const UserPendingListPage: React.FC = () => {
  useInitialPage();

  const {
    pendingUsers,
    currentPage,
    totalPages,
    keyword,
    filter,
    isLoading,
    hasError,
    isEmpty,
    shouldReset,
    totalCount,
    handlePageChange,
    handleSearch,
    handleFilterChange,
    handleReset,
    handleResetComplete,
    handleRefresh,
    handleStatusUpdate,
    handleFileDownload,
    isUpdatingStatus,
    isDownloadingFile,
    error,
  } = usePendingApproval();

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<PendingUser | null>(null);

  const handleRowClick = (user: PendingUser) => {
    setSelectedUser(user);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedUser(null);
  };

  const handleFilterSelect = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedFilter = event.target.value as ApprovalStatus | 'ALL';
    handleFilterChange(selectedFilter);
  };

  return (
    <section className="relative h-[calc(100vh-56px)]">
      <section className="relative h-full overflow-y-scroll bg-[#f1f1f1] px-8 pt-6">
        <h2 className="text-2xl font-bold text-[#1d1d1d]">Pending Approval</h2>

        <div className="mb-5 mt-8 flex items-center justify-end gap-2">
          <button
            type="button"
            className="h-7 w-7 rounded bg-[#e2e2e2] hover:opacity-80 disabled:opacity-50"
            aria-label="refresh button"
            onClick={handleRefresh}
            disabled={isLoading}
          >
            <RotateCcw size={16} className="mx-auto text-[#1d1d1d]" />
          </button>
          <select
            value={filter}
            onChange={handleFilterSelect}
            className="h-7 w-32 rounded border-none bg-[#e2e2e2] px-2 text-sm disabled:opacity-50"
            aria-label="status filter"
            disabled={isLoading}
          >
            {statusFilterOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <SearchBar
          onSearch={handleSearch}
          shouldReset={shouldReset}
          onResetComplete={handleResetComplete}
          initialValue={keyword}
        />

        <UserPendingTable
          pendingUsers={pendingUsers}
          onRowClick={handleRowClick}
          isLoading={isLoading}
          hasError={hasError}
          isEmpty={isEmpty}
          error={error}
        />

        {!isLoading && !hasError && totalPages > 0 && (
          <footer className="flex-none border border-x-[#DEDEDE] bg-[#E8E8E8] pb-4 pt-3">
            <Paging
              currentPage={currentPage}
              setCurrentPage={handlePageChange}
              totalPage={totalPages}
            />
          </footer>
        )}
      </section>

      <PendingApprovalModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        selectedUser={selectedUser}
        onStatusUpdate={handleStatusUpdate}
        onFileDownload={handleFileDownload}
        isUpdating={isUpdatingStatus}
        isDownloading={isDownloadingFile}
      />
    </section>
  );
};

export default UserPendingListPage;
